import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import {
  CorrelationResult,
  DataQualityReport,
  Dataset,
  ExportOptions,
  ExportResult,
} from '@/lib/types';
import { findCorrelations, generateQualityReport } from '@/lib/mockData';
import {
  ChartLineIcon,
  CheckCircleIcon,
  CheckSquareIcon,
  ClockIcon,
  DownloadIcon,
  FileArrowDownIcon,
  PlayIcon,
  SquareIcon,
  TrendUpIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

interface BulkActionsProps {
  datasets: Dataset[];
  qualityReports: Record<string, DataQualityReport>;
  onQualityReportsUpdate: (reports: Record<string, DataQualityReport>) => void;
  onExport: (options: ExportOptions) => Promise<ExportResult>;
  onCorrelationAnalysis?: (correlations: CorrelationResult[]) => void;
}

interface BulkAnalysisState {
  isRunning: boolean;
  progress: number;
  currentDataset: string | null;
  completed: string[];
  errors: string[];
}

export function BulkActions({
  datasets,
  qualityReports,
  onQualityReportsUpdate,
  onExport,
  onCorrelationAnalysis,
}: Readonly<BulkActionsProps>) {
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [bulkExportOpen, setBulkExportOpen] = useState(false);
  const [bulkAnalysisOpen, setBulkAnalysisOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<
    'csv' | 'json' | 'excel' | 'parquet'
  >('csv');
  const [includeSchema, setIncludeSchema] = useState(true);
  const [analysisState, setAnalysisState] = useState<BulkAnalysisState>({
    isRunning: false,
    progress: 0,
    currentDataset: null,
    completed: [],
    errors: [],
  });

  const allSelected =
    selectedDatasets.length === datasets.length && datasets.length > 0;
  const someSelected = selectedDatasets.length > 0;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedDatasets([]);
    } else {
      setSelectedDatasets(datasets.map(d => d.id));
    }
  };

  const handleSelectDataset = (datasetId: string) => {
    setSelectedDatasets(prev =>
      prev.includes(datasetId)
        ? prev.filter(id => id !== datasetId)
        : [...prev, datasetId]
    );
  };

  const handleBulkExport = async () => {
    if (selectedDatasets.length === 0) {
      toast.error('Please select at least one dataset to export');

      return;
    }

    try {
      const options: ExportOptions = {
        format: exportFormat,
        includeMetadata: true,
        includeSchema,
      };

      await onExport(options);
      toast.success(
        `Successfully exported ${selectedDatasets.length} datasets as ${exportFormat.toUpperCase()}`
      );
      setBulkExportOpen(false);
      setSelectedDatasets([]);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to export datasets. Please try again.';

      toast.error(errorMessage);
    }
  };

  const handleBulkQualityAnalysis = async () => {
    if (selectedDatasets.length === 0) {
      toast.error('Please select at least one dataset to analyze');

      return;
    }

    const selectedData = datasets.filter(d => selectedDatasets.includes(d.id));

    setAnalysisState({
      isRunning: true,
      progress: 0,
      currentDataset: null,
      completed: [],
      errors: [],
    });

    const newReports: Record<string, DataQualityReport> = { ...qualityReports };

    try {
      for (let i = 0; i < selectedData.length; i++) {
        const dataset = selectedData[i];

        setAnalysisState(prev => ({
          ...prev,
          currentDataset: dataset.name,
          progress: (i / selectedData.length) * 100,
        }));

        // Simulate analysis time
        await new Promise(resolve =>
          setTimeout(resolve, 800 + Math.random() * 1200)
        );

        try {
          const report = generateQualityReport(dataset);

          newReports[dataset.id] = report;

          setAnalysisState(prev => ({
            ...prev,
            completed: [...prev.completed, dataset.id],
          }));
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : `Quality analysis failed for ${dataset.name}`;

          console.warn(errorMessage);
          setAnalysisState(prev => ({
            ...prev,
            errors: [...prev.errors, dataset.id],
          }));
        }
      }

      setAnalysisState(prev => ({
        ...prev,
        isRunning: false,
        progress: 100,
        currentDataset: null,
      }));

      onQualityReportsUpdate(newReports);

      const successCount = analysisState.completed.length;
      const errorCount = analysisState.errors.length;

      if (errorCount === 0) {
        toast.success(
          `Quality analysis completed for ${successCount} datasets`
        );
      } else {
        toast.warning(
          `Analysis completed with ${errorCount} errors. ${successCount} datasets analyzed successfully.`
        );
      }

      setBulkAnalysisOpen(false);
      setSelectedDatasets([]);

      // Reset analysis state after a delay
      setTimeout(() => {
        setAnalysisState({
          isRunning: false,
          progress: 0,
          currentDataset: null,
          completed: [],
          errors: [],
        });
      }, 2000);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Bulk analysis failed. Please try again.';

      console.error('Bulk analysis error:', errorMessage);
      setAnalysisState(prev => ({
        ...prev,
        isRunning: false,
      }));
      toast.error(errorMessage);
    }
  };

  const handleBulkCorrelationAnalysis = () => {
    if (selectedDatasets.length < 2) {
      toast.error('Please select at least 2 datasets to analyze correlations');

      return;
    }

    const selectedData = datasets.filter(d => selectedDatasets.includes(d.id));
    const correlations = findCorrelations(selectedData);

    if (onCorrelationAnalysis) {
      onCorrelationAnalysis(correlations);
      toast.success(
        `Found ${correlations.length} potential correlations between selected datasets`
      );
    }

    setSelectedDatasets([]);
  };

  const getAnalysisStatusIcon = (datasetId: string) => {
    if (analysisState.completed.includes(datasetId)) {
      return <CheckCircleIcon className='w-4 h-4 text-green-500' />;
    }
    if (analysisState.errors.includes(datasetId)) {
      return <WarningIcon className='w-4 h-4 text-red-500' />;
    }
    if (
      analysisState.currentDataset ===
      datasets.find(d => d.id === datasetId)?.name
    ) {
      return <ClockIcon className='w-4 h-4 text-blue-500 animate-pulse' />;
    }

    return null;
  };

  if (datasets.length === 0) {
    return null;
  }

  return (
    <Card className='border-accent/20 bg-accent/5'>
      <CardHeader className='pb-3'>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle className='text-sm font-medium flex items-center gap-2'>
              <CheckSquareIcon className='w-4 h-4' />
              Bulk Actions
            </CardTitle>
            <CardDescription className='text-xs'>
              Select datasets for bulk operations
            </CardDescription>
          </div>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleSelectAll}
              className='gap-1 text-xs'
            >
              {allSelected ? (
                <SquareIcon className='w-3 h-3' />
              ) : (
                <CheckSquareIcon className='w-3 h-3' />
              )}
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        {/* Dataset Selection */}
        <div className='space-y-2'>
          <div className='text-xs font-medium text-muted-foreground'>
            Select Datasets ({selectedDatasets.length}/{datasets.length})
          </div>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-32 overflow-y-auto'>
            {datasets.map(dataset => (
              <div
                key={dataset.id}
                className='flex items-center gap-2 p-2 rounded border bg-card hover:bg-muted/50 transition-colors'
              >
                <Checkbox
                  checked={selectedDatasets.includes(dataset.id)}
                  onCheckedChange={() => handleSelectDataset(dataset.id)}
                  className='flex-shrink-0'
                />
                <div className='min-w-0 flex-1'>
                  <div
                    className='text-xs font-medium truncate'
                    title={dataset.name}
                  >
                    {dataset.name}
                  </div>
                  <div className='text-xs text-muted-foreground'>
                    {dataset.totalRows.toLocaleString()} rows
                  </div>
                </div>
                {getAnalysisStatusIcon(dataset.id)}
              </div>
            ))}
          </div>
        </div>

        {/* Bulk Action Buttons */}
        {someSelected && (
          <div className='flex flex-wrap gap-2 pt-2 border-t'>
            <Dialog open={bulkExportOpen} onOpenChange={setBulkExportOpen}>
              <DialogTrigger asChild>
                <Button variant='outline' size='sm' className='gap-1'>
                  <DownloadIcon className='w-3 h-3' />
                  Export Selected ({selectedDatasets.length})
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className='flex items-center gap-2'>
                    <DownloadIcon className='w-5 h-5' />
                    Bulk Export Datasets
                  </DialogTitle>
                  <DialogDescription>
                    Export {selectedDatasets.length} selected datasets in your
                    preferred format
                  </DialogDescription>
                </DialogHeader>

                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <label
                      htmlFor='export-format'
                      className='text-sm font-medium'
                    >
                      Export Format
                    </label>
                    <Select
                      value={exportFormat}
                      onValueChange={(value: any) => setExportFormat(value)}
                    >
                      <SelectTrigger id='export-format'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='csv'>
                          CSV (Comma Separated)
                        </SelectItem>
                        <SelectItem value='json'>
                          JSON (JavaScript Object)
                        </SelectItem>
                        <SelectItem value='excel'>Excel (.xlsx)</SelectItem>
                        <SelectItem value='parquet'>
                          Parquet (Columnar)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='flex items-center gap-2'>
                    <Checkbox
                      id='include-schema'
                      checked={includeSchema}
                      onCheckedChange={checked => setIncludeSchema(!!checked)}
                    />
                    <label htmlFor='include-schema' className='text-sm'>
                      Include schema metadata
                    </label>
                  </div>

                  <div className='space-y-2'>
                    <div className='text-sm font-medium'>
                      Selected Datasets:
                    </div>
                    <div className='max-h-32 overflow-y-auto space-y-1'>
                      {datasets
                        .filter(d => selectedDatasets.includes(d.id))
                        .map(dataset => (
                          <div
                            key={dataset.id}
                            className='flex items-center justify-between text-xs p-2 bg-muted rounded'
                          >
                            <span className='truncate'>{dataset.name}</span>
                            <Badge variant='outline' className='text-xs'>
                              {dataset.totalRows.toLocaleString()} rows
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className='flex justify-end gap-2'>
                    <Button
                      variant='outline'
                      onClick={() => setBulkExportOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleBulkExport} className='gap-1'>
                      <FileArrowDownIcon className='w-4 h-4' />
                      Export {selectedDatasets.length} Datasets
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={bulkAnalysisOpen} onOpenChange={setBulkAnalysisOpen}>
              <DialogTrigger asChild>
                <Button variant='outline' size='sm' className='gap-1'>
                  <ChartLineIcon className='w-3 h-3' />
                  Analyze Quality ({selectedDatasets.length})
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className='flex items-center gap-2'>
                    <ChartLineIcon className='w-5 h-5' />
                    Bulk Quality Analysis
                  </DialogTitle>
                  <DialogDescription>
                    Run comprehensive quality analysis on{' '}
                    {selectedDatasets.length} selected datasets
                  </DialogDescription>
                </DialogHeader>

                <div className='space-y-4'>
                  {analysisState.isRunning && (
                    <div className='space-y-2'>
                      <div className='flex items-center justify-between text-sm'>
                        <span>Analyzing datasets...</span>
                        <span>{Math.round(analysisState.progress)}%</span>
                      </div>
                      <Progress
                        value={analysisState.progress}
                        className='w-full'
                      />
                      {analysisState.currentDataset && (
                        <div className='text-xs text-muted-foreground'>
                          Currently analyzing: {analysisState.currentDataset}
                        </div>
                      )}
                    </div>
                  )}

                  <div className='space-y-2'>
                    <div className='text-sm font-medium'>
                      Selected Datasets:
                    </div>
                    <div className='max-h-40 overflow-y-auto space-y-1'>
                      {datasets
                        .filter(d => selectedDatasets.includes(d.id))
                        .map(dataset => (
                          <div
                            key={dataset.id}
                            className='flex items-center justify-between text-xs p-2 bg-muted rounded'
                          >
                            <div className='flex items-center gap-2 min-w-0'>
                              {getAnalysisStatusIcon(dataset.id)}
                              <span className='truncate'>{dataset.name}</span>
                            </div>
                            <div className='flex items-center gap-2'>
                              {qualityReports[dataset.id] && (
                                <Badge variant='outline' className='text-xs'>
                                  {Math.round(
                                    (qualityReports[dataset.id].completeness +
                                      qualityReports[dataset.id].validity +
                                      qualityReports[dataset.id].consistency +
                                      qualityReports[dataset.id].uniqueness) /
                                      4
                                  )}
                                  % quality
                                </Badge>
                              )}
                              <Badge variant='outline' className='text-xs'>
                                {dataset.totalRows.toLocaleString()} rows
                              </Badge>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className='flex justify-end gap-2'>
                    <Button
                      variant='outline'
                      onClick={() => setBulkAnalysisOpen(false)}
                      disabled={analysisState.isRunning}
                    >
                      {analysisState.isRunning ? 'Running...' : 'Cancel'}
                    </Button>
                    <Button
                      onClick={handleBulkQualityAnalysis}
                      className='gap-1'
                      disabled={analysisState.isRunning}
                    >
                      <PlayIcon className='w-4 h-4' />
                      {analysisState.isRunning
                        ? 'Analyzing...'
                        : `Analyze ${selectedDatasets.length} Datasets`}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {onCorrelationAnalysis && selectedDatasets.length >= 2 && (
              <Button
                variant='outline'
                size='sm'
                className='gap-1'
                onClick={handleBulkCorrelationAnalysis}
              >
                <TrendUpIcon className='w-3 h-3' />
                Find Correlations ({selectedDatasets.length})
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
